import { createClient } from '@supabase/supabase-js';
import { processImagesWithGemini } from './ai';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Variáveis de ambiente do Supabase não configuradas');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface ExamImage {
  url: string;
  diagnosis: string | null;
  recommendations: string | null;
}

export interface ProcessImagesResult {
  images: ExamImage[];
  consultationId: string;
}

export interface Exam {
  id: string;
  consultation_id: string;
  user_id: string;
  context?: string;
  images: ExamImage[];
  status: 'pending' | 'processing' | 'completed';
  created_at: string;
}

export async function getExams(userId: string) {
  const { data, error } = await supabase
    .from('exams')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Exam[];
}

export async function processImagesDirectly(files: File[], context?: string, userId?: string): Promise<ProcessImagesResult> {
  try {
    if (!userId) throw new Error('ID do usuário não fornecido');
    if (!files.length) throw new Error('Nenhum arquivo selecionado');
    if (files.length > 3) {
      throw new Error('Máximo de 3 imagens por consulta');
    }

    // Obter o perfil do médico
    const doctorProfile = await getDoctorProfile(userId);
    if (!doctorProfile) {
      throw new Error('Perfil do médico não encontrado');
    }

    // Processar imagens com Gemini
    const result = await processImagesWithGemini(files, context);

    // Converter imagens para base64
    const base64Images = await Promise.all(
      files.map(async (file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            if (!result.startsWith('data:')) {
              resolve(`data:${file.type};base64,${result.split(',')[1] || result}`);
            } else {
              resolve(result);
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );

    // Criar registro da consulta com as imagens em base64
    const consultation = await createConsultation(doctorProfile.id, {
      patientContext: context,
      imageUrls: base64Images,
      aiResult: result.images[0].diagnosis || ''
    });

    return {
      images: result.images,
      consultationId: consultation.id
    };
  } catch (error) {
    console.error('Erro ao processar imagens:', error);
    throw error;
  }
}

export interface DoctorProfile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  state: string;
  email: string;
  created_at: string;
  updated_at: string;
}

export interface Consultation {
  id: string;
  doctor_id: string;
  patient_context?: string;
  image_urls: string[];
  ai_result: string;
  doctor_feedback?: string;
  created_at: string;
  updated_at: string;
}

export async function createDoctorProfile(
  userId: string,
  data: {
    firstName: string;
    lastName: string;
    state: string;
    email: string;
  }
) {
  const { data: profile, error } = await supabase
    .from('doctor_profiles')
    .insert({
      user_id: userId,
      first_name: data.firstName,
      last_name: data.lastName,
      state: data.state,
      email: data.email
    })
    .select()
    .single();

  if (error) throw error;
  return profile;
}

export async function updateDoctorProfile(
  userId: string,
  profile: Pick<DoctorProfile, 'first_name' | 'last_name' | 'state' | 'email'>
): Promise<void> {
  const { error } = await supabase
    .from('doctor_profiles')
    .update({
      first_name: profile.first_name,
      last_name: profile.last_name,
      state: profile.state,
      email: profile.email,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId)

  if (error) {
    console.error('Error updating doctor profile:', error)
    throw error
  }
}

export async function getDoctorProfile(userId: string): Promise<DoctorProfile | null> {
  const { data, error } = await supabase
    .from('doctor_profiles')
    .select('*')
    .eq('user_id', userId)
    .single()

  if (error) {
    console.error('Error fetching doctor profile:', error)
    return null
  }

  return data
}

export async function createConsultation(
  doctorId: string,
  data: {
    patientContext?: string;
    imageUrls: string[];
    aiResult: string;
  }
) {
  const { data: consultation, error } = await supabase
    .from('consultations')
    .insert({
      doctor_id: doctorId,
      patient_context: data.patientContext,
      image_urls: data.imageUrls,
      ai_result: data.aiResult
    })
    .select()
    .single();

  if (error) throw error;
  return consultation;
}

export async function updateConsultationFeedback(
  consultationId: string,
  feedback: string
) {
  const { data, error } = await supabase
    .from('consultations')
    .update({ doctor_feedback: feedback })
    .eq('id', consultationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}